// Обращения учеников (.scratch/autopilot/feedback.md): форма "Пожелание" и
// "Нашёл ошибку" в приложении и письма на support@turkido.ru. Здесь только
// чистые функции без базы и без node/*: их импортирует и клиентская форма
// (feedback-form.tsx), и серверный импорт писем (support-inbox.ts).
// Проверяет feedback.test.ts.

export type FeedbackKind = 'wish' | 'bug'
// telegram: задача владельца из бота автопилота (lib/owner-tasks.ts).
export type FeedbackSource = 'app' | 'mail' | 'telegram'
export type FeedbackStatus = 'new' | 'seen' | 'done' | 'wontfix'
export type FeedbackRowKind = FeedbackKind | 'mail' | 'owner'

export const FEEDBACK_TEXT_MIN = 3
export const FEEDBACK_TEXT_MAX = 2000
// Письмо длиннее формы: ограничение таблицы.
export const MAIL_TEXT_MAX = 4000
// Обращений с одного аккаунта в сутки.
export const FEEDBACK_DAILY_LIMIT = 10
export const PAGE_MAX = 200
export const DEVICE_MAX = 300
export const BUILD_MAX = 64
export const NOTE_MAX = 1000

export const FEEDBACK_STATUSES: readonly FeedbackStatus[] = ['new', 'seen', 'done', 'wontfix']
export const KIND_LABELS: Record<FeedbackRowKind, string> = { wish: 'Пожелание', bug: 'Ошибка', mail: 'Письмо', owner: 'Задача из Telegram' }
export const STATUS_LABELS: Record<FeedbackStatus, string> = { new: 'новое', seen: 'в работе', done: 'сделано', wontfix: 'не будем' }

export type FeedbackReward = 'akce' | 'plus_month'
// Сумму akçe владелец выбирает сам (29.10, миграция 20261029120000), по
// умолчанию 100. Пределы те же, что в check у feedback.reward_akce.
export const FEEDBACK_REWARD_AKCE = 100
export const FEEDBACK_REWARD_AKCE_MIN = 10
export const FEEDBACK_REWARD_AKCE_MAX = 1000

export function parseRewardAkce(v: unknown): number | null {
  const n = typeof v === 'string' ? Number(v.trim()) : typeof v === 'number' ? v : NaN
  return Number.isInteger(n) && n >= FEEDBACK_REWARD_AKCE_MIN && n <= FEEDBACK_REWARD_AKCE_MAX ? n : null
}

export function rewardLabel(reward: FeedbackReward, akce?: number | null): string {
  return reward === 'akce' ? `${akce ?? FEEDBACK_REWARD_AKCE} akçe` : 'месяц Plus'
}

export function isFeedbackReward(v: unknown): v is FeedbackReward {
  return v === 'akce' || v === 'plus_month'
}

// Дата дд.мм.гггг по Москве для письма и сообщения в боте.
export function rewardDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric' })
}

export const REWARD_MAIL_SUBJECT = 'Спасибо за предложение'

// Текст ученику (письмо и Telegram одинаковые): на "вы", без тире и жирного.
export function rewardMessage(reward: FeedbackReward, until: string | null | undefined, akce: number = FEEDBACK_REWARD_AKCE): string {
  if (reward === 'akce') return `Ваше предложение приняли. Зачислили ${akce} akçe, они уже на счёте в Turkido.`
  const date = rewardDate(until)
  return date ? `Ваше предложение приняли. В подарок месяц Plus: подписка действует до ${date}.` : 'Ваше предложение приняли. В подарок месяц Plus на 30 дней.'
}

export type FeedbackRow = {
  id: string
  created_at: string
  updated_at: string
  source: FeedbackSource
  kind: FeedbackRowKind
  user_id: string | null
  tier: string | null
  page: string | null
  device: string | null
  build: string | null
  text: string
  mail_from: string | null
  mail_subject: string | null
  mail_message_id: string | null
  mail_uid: number | null
  status: FeedbackStatus
  note: string | null
  // Награда за принятое предложение (тикет 96, миграция 20261008170000):
  // 'akce' = reward_akce akçe, 'plus_month' = 30 дней Plus. Одна на обращение.
  reward: FeedbackReward | null
  reward_akce?: number | null
  rewarded_at: string | null
  // Задачи владельца из Telegram (миграция 20261002200000_owner_tasks).
  num: number | null
  tg_message_id: number | null
  tg_media_group: string | null
  urgent: boolean
  attachments: FeedbackAttachment[]
}

export type FeedbackAttachment = { path: string; mime: string; size: number }

// Что видит ночной автомат: без id ученика и адреса отправителя. user_key
// (короткий хеш user_id) нужен, чтобы сгруппировать обращения одного человека.
export type AgentFeedback = Omit<FeedbackRow, 'user_id' | 'mail_from' | 'mail_message_id' | 'mail_uid'> & { user_key: string | null }

export function isFeedbackKind(v: unknown): v is FeedbackKind {
  return v === 'wish' || v === 'bug'
}

export function isFeedbackStatus(v: unknown): v is FeedbackStatus {
  return typeof v === 'string' && (FEEDBACK_STATUSES as readonly string[]).includes(v)
}

// Текст из формы: без пробелов по краям, переводы строк как в Unix, не больше
// двух пустых строк подряд. Ошибка возвращается строкой для показа ученику.
export function normalizeFeedbackText(raw: unknown): { text: string } | { error: string } {
  if (typeof raw !== 'string') return { error: 'Напишите текст.' }
  const text = raw.replace(/\r\n?/g, '\n').replace(/\n{3,}/g, '\n\n').trim()
  if (text.length < FEEDBACK_TEXT_MIN) return { error: 'Напишите хотя бы пару слов.' }
  if (text.length > FEEDBACK_TEXT_MAX) return { error: `Не больше ${FEEDBACK_TEXT_MAX} знаков, сейчас ${text.length}.` }
  return { text }
}

// Строка без переводов строк, обрезанная до max; пустая даёт null.
export function clip(v: unknown, max: number): string | null {
  if (typeof v !== 'string') return null
  const s = v.replace(/\s+/g, ' ').trim().slice(0, max)
  return s === '' ? null : s
}

// Путь экрана: только то, что начинается с "/", без query и хеша.
export function pagePath(v: unknown): string | null {
  if (typeof v !== 'string' || !v.startsWith('/')) return null
  const s = v.split(/[?#]/)[0].replace(/\s+/g, '').slice(0, PAGE_MAX)
  return s === '' ? null : s
}

export const FOUNDER_FEEDBACK_URL = 'https://turkido.ru/founder/feedback'

// Сообщение владельцу в Telegram: только вид, тариф и экран, без текста, темы,
// имени и адреса. Текст ученика в Telegram не отправляем: это был бы ещё один
// получатель персональных данных за границей, которого нет в политике
// (/legal/privacy). Читать обращения на /founder/feedback.
export function telegramLine(f: { source: FeedbackSource; kind: FeedbackRowKind; tier?: string | null; page?: string | null }): string {
  const who = f.source === 'mail' ? 'письмо на support@' : f.kind === 'bug' ? 'ошибка от ученика' : 'пожелание от ученика'
  const tier = f.source === 'app' && f.tier ? ` (${f.tier})` : ''
  const where = f.source === 'app' && f.page ? `, экран ${f.page}` : ''
  return `Turkido: ${who}${tier}${where}. Текст: ${FOUNDER_FEEDBACK_URL}`
}

// ── Письма ─────────────────────────────────────────────────────────────────

export type MailMeta = {
  from: string | null
  subject: string | null
  text: string
  autoSubmitted?: string | null
  precedence?: string | null
  // Типы вложений (contentType).
  attachments?: string[]
}

// ok: обращение; self: наше же письмо; auto: автоответ или рассылка;
// dmarc: отчёт DMARC (rua=mailto:support@); empty: ни текста, ни темы.
export type MailVerdict = 'ok' | 'self' | 'auto' | 'dmarc' | 'empty'

export function classifyMail(m: MailMeta): MailVerdict {
  const from = (m.from ?? '').trim().toLowerCase()
  const subject = (m.subject ?? '').trim()
  const text = m.text.trim()
  if (from.endsWith('@turkido.ru')) return 'self'
  const archive = (m.attachments ?? []).some((t) => /zip|gzip|x-gzip|xml/i.test(t))
  if (/^report domain:/i.test(subject) || /dmarc/.test(from) || (archive && text.length < 200)) return 'dmarc'
  const auto = (m.autoSubmitted ?? '').trim().toLowerCase()
  if (auto !== '' && auto !== 'no') return 'auto'
  const precedence = (m.precedence ?? '').trim().toLowerCase()
  if (precedence === 'bulk' || precedence === 'list' || precedence === 'junk') return 'auto'
  if (/^(no-?reply|do-?not-?reply|mailer-daemon|postmaster|bounce)/.test(from)) return 'auto'
  if (/^(automatic reply|auto(matic)?[:\s-]|out of office|автоответ|автоматический ответ)/i.test(subject)) return 'auto'
  if (text === '' && subject === '') return 'empty'
  return 'ok'
}

// Текст письма без цитат прошлой переписки и подписи. Строки с ">" выкидываем,
// на заголовке цитаты ("On ... wrote:", "... писал(а):", "-----Original
// Message-----") и на разделителе подписи "-- " останавливаемся.
export function cleanMailText(raw: string): string {
  const out: string[] = []
  for (const line of raw.replace(/\r\n?/g, '\n').split('\n')) {
    const t = line.trimEnd()
    if (/^(-- ?|_{3,}|-----\s*Original Message\s*-----|-----\s*Пересылаемое сообщение\s*-----)$/.test(t)) break
    if (/^(On .+ wrote:|.+ (писал|написал)\(а\):|.+ (писал|написал|пишет):|.+ <[^>]+@[^>]+>:)\s*$/.test(t)) break
    if (t.startsWith('>')) continue
    out.push(t)
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim().slice(0, MAIL_TEXT_MAX)
}

// Грубый текст из html-письма: без скриптов, стилей и цитат (blockquote),
// блочные теги дают перевод строки, остальные теги убираются.
export function htmlToText(html: string): string {
  return html
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
    .replace(/<blockquote[\s\S]*?<\/blockquote>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6]|pre)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

// Строка для автомата: без ПДн, см. AgentFeedback.
export function forAgent(row: FeedbackRow, userKey: string | null): AgentFeedback {
  return {
    id: row.id,
    created_at: row.created_at,
    updated_at: row.updated_at,
    source: row.source,
    kind: row.kind,
    tier: row.tier,
    page: row.page,
    device: row.device,
    build: row.build,
    text: row.text,
    mail_subject: row.mail_subject,
    status: row.status,
    note: row.note,
    reward: row.reward ?? null,
    rewarded_at: row.rewarded_at ?? null,
    num: row.num,
    tg_message_id: row.tg_message_id,
    tg_media_group: row.tg_media_group,
    urgent: row.urgent,
    attachments: row.attachments ?? [],
    user_key: userKey,
  }
}
